import { formatTranslation, unsafeTranslationKey } from 'intl';
import { InspectionTranslationKeys } from 'intl/keys';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import { InspectionStatus } from 'shared/domain/inspection/inspectionModel';
import React, { ReactElement, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Disabled } from 'components/dataCreationForms/withInputForm/statefulInputsWrapper/types';
import {
  getDisabledReason,
  isFieldDisabled,
} from 'components/dataCreationForms/withInputForm/statefulInputsWrapper/model';

interface InspectionStatusDropdownProps {
  value: InspectionStatus;
  onChange: (status: InspectionStatus) => void;
  disabled?: Disabled;
  /** Shown under the field. Used when completion is blocked by missing answers. */
  helperText?: string;
}

export function InspectionStatusDropdown({
  value,
  onChange,
  disabled,
  helperText,
}: InspectionStatusDropdownProps): ReactElement {
  const intl = useIntl();

  const handleChange = useCallback(
    (event: SelectChangeEvent<InspectionStatus>) => {
      onChange(event.target.value as InspectionStatus);
    },
    [onChange]
  );

  const disabledReason = getDisabledReason(disabled);
  const fieldDisabled = isFieldDisabled(disabled);

  const label = formatTranslation(intl, InspectionTranslationKeys.status);

  const tooltipTitle = useMemo(() => {
    if (fieldDisabled && disabledReason) {
      return formatTranslation(intl, unsafeTranslationKey(disabledReason));
    }
    return undefined;
  }, [fieldDisabled, disabledReason, intl]);

  return (
    <Tooltip title={tooltipTitle}>
      <FormControl fullWidth margin='dense' disabled={fieldDisabled}>
        <InputLabel id='inspection-status-label'>{label}</InputLabel>
        <Select
          labelId='inspection-status-label'
          id='inspection-status-select'
          value={value}
          label={label}
          onChange={handleChange}
          data-qa='inspection_status_field'
        >
          <MenuItem value={InspectionStatus.inProgress}>
            {formatTranslation(intl, InspectionTranslationKeys.statusInProgress)}
          </MenuItem>
          <MenuItem value={InspectionStatus.completed}>
            {formatTranslation(intl, InspectionTranslationKeys.statusCompleted)}
          </MenuItem>
        </Select>
        {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>
    </Tooltip>
  );
}
